import { ApiResult, request, safeRequest } from '@synergy/api';
import { noop } from '@synergy/utils';

import { ShoppingCartDto } from '../models/dto/shopping-cart-dto';
import { WishListDto } from '../models/dto/wish-list-dto';
import { WishListId, WishListListItem } from '../models/wish-list-list-item';
import { WishListType } from '../models/enums/wish-list-type';

export class WishListApi {
    constructor(protected baseUrl: string) {}

    public getLists(): Promise<ApiResult<WishListListItem[]>> {
        return safeRequest(
            () => request.get<WishListListItem[]>(`${this.baseUrl}/wish-lists`),
            response => response.data
        );
    }

    public create(name: string): Promise<ApiResult<WishListListItem>> {
        return safeRequest(
            () =>
                request.post<WishListListItem>(`${this.baseUrl}/wish-lists/create`, {
                    name,
                }),
            response => response.data
        );
    }

    public update(id: number, name: string): Promise<ApiResult<WishListListItem>> {
        return safeRequest(
            () => request.patch<WishListListItem>(`${this.baseUrl}/wish-lists/${id}`, { name }),
            response => response.data
        );
    }

    public copy(id: WishListId, name: string): Promise<ApiResult<WishListListItem>> {
        return safeRequest(
            () => request.post<WishListListItem>(`${this.baseUrl}/wish-lists/${id}/copy`, { name }),
            response => response.data
        );
    }

    public delete(id: number): Promise<ApiResult<void>> {
        return safeRequest(() => request.delete(`${this.baseUrl}/wish-lists/${id}`), noop);
    }

    public replaceCart(id: WishListId): Promise<ApiResult<ShoppingCartDto>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/wish-lists/${id}/replace-cart`),
            response => response.data
        );
    }

    public getDetails(id: WishListId): Promise<ApiResult<WishListDto>> {
        return safeRequest(
            () => request.get<WishListDto>(`${this.baseUrl}/wish-lists/${id}/items`),
            response => response.data
        );
    }

    public addItem(wishListId: WishListId, productId: number, quantity: number): Promise<ApiResult<void>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/wish-lists/${wishListId}/items/add`, { productId, quantity }),
            noop
        );
    }

    public changeItemQuantity(wishListId: WishListId, itemId: number, quantity: number): Promise<ApiResult<void>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/wish-lists/${wishListId}/items/${itemId}/quantity`, { quantity }),
            noop
        );
    }

    public deleteItem(wishListId: WishListId, itemId: number): Promise<ApiResult<void>> {
        return safeRequest(() => request.delete(`${this.baseUrl}/wish-lists/${wishListId}/items/${itemId}`), noop);
    }

    public moveItem(
        sourceWishListId: WishListId,
        itemId: number,
        targetWishListId: WishListId
    ): Promise<ApiResult<void>> {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/wish-lists/${sourceWishListId}/items/${itemId}/move`, {
                    targetWishListId,
                }),
            noop
        );
    }

    public share(
        id: number,
        type: WishListType | null,
        sharedAccountIds: number[],
        isSharedWithOwnAccounts: boolean,
        isSharedWithOtherAccountUsers: boolean,
        infoText: string | null
    ): Promise<ApiResult<WishListDto>> {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/wish-lists/${id}/share`, {
                    type,
                    infoText,
                    sharedAccountIds,
                    isSharedWithOwnAccounts,
                    isSharedWithOtherAccountUsers,
                }),
            response => response.data
        );
    }
}
